import { QueryPackageArgs } from "@pathwright/graphql-client/src/generated"
import { getEmbedURL, serializeEmbedPrimaryColor } from "../embed/utils"
import { PackageVariant } from "./types"

/** These are the url params we potentially receive for selecting a package */
const PACKAGE_URL_PARAM_KEYS = [
  "pkg",
  "utm_source",
  "utm_medium",
  "utm_campaign",
  "via"
] as const

export const getPackageParams = (
  propParams?: URLSearchParams
): URLSearchParams => {
  const source = propParams ?? new URLSearchParams(window.location.search)
  const next = new URLSearchParams()

  for (const key of PACKAGE_URL_PARAM_KEYS) {
    for (const value of source.getAll(key)) {
      next.append(key, value)
    }
  }

  return next
}

export const mapUrlParamsToVariables = (
  urlParams: URLSearchParams
): QueryPackageArgs => {
  return {
    signup_config_key: urlParams.get("pkg"),
    utm_source: urlParams.get("utm_source"),
    utm_medium: urlParams.get("utm_medium"),
    utm_campaign: urlParams.get("utm_campaign"),
    rewardful_referral_id: urlParams.get("via")
  }
}

const SKIP_URL_MERGE_RE = /^(mailto:|tel:|javascript:)/i

export const mergePackageParamsIntoURL = (
  targetUrl: string,
  packageParams: URLSearchParams
): string => {
  const trimmedTargetUrl = targetUrl?.trim()
  if (!trimmedTargetUrl || SKIP_URL_MERGE_RE.test(trimmedTargetUrl)) {
    return trimmedTargetUrl
  }

  let target: URL

  try {
    target = new URL(
      trimmedTargetUrl,
      typeof window !== "undefined"
        ? window.location.origin
        : "https://pathwright.com"
    )
  } catch (error) {
    console.error("Error merging package params into URL", error)
    return trimmedTargetUrl
  }
  const filteredPackageParams = getPackageParams(packageParams)

  for (const key of PACKAGE_URL_PARAM_KEYS) {
    if (target.searchParams.has(key)) continue
    for (const value of filteredPackageParams.getAll(key)) {
      target.searchParams.append(key, value)
    }
  }

  return target.toString()
}

type PackageEmbedURLOptions = {
  baseUrl?: string
  schoolId?: number
  variant?: PackageVariant
  displayDiscount?: boolean
  primaryColor?: string
  backgroundColor?: string
  colorMode?: "light" | "dark"
  buttonLabel?: string
  buttonLink?: string
  buttonTarget?: "same_tab" | "new_tab"
  packageParams?: URLSearchParams
}

const getEmbedPackageParams = (packageParams?: URLSearchParams) => {
  if (packageParams) return getPackageParams(packageParams)
  if (typeof window !== "undefined") return getPackageParams()
  return new URLSearchParams()
}

export const getPackageEmbedURL = ({
  baseUrl,
  schoolId,
  variant = "card",
  displayDiscount = true,
  primaryColor,
  backgroundColor,
  colorMode,
  buttonLabel,
  buttonLink,
  buttonTarget,
  packageParams
}: PackageEmbedURLOptions = {}) => {
  const params = getEmbedPackageParams(packageParams)
  params.set("variant", variant)

  if (schoolId) {
    params.set("school_id", schoolId.toString())
  }

  if (!displayDiscount) {
    params.set("displayDiscount", "false")
  }

  const primaryColorParam = serializeEmbedPrimaryColor(primaryColor)
  if (primaryColorParam) {
    params.set("primary_color", primaryColorParam)
  }

  const backgroundColorParam = serializeEmbedPrimaryColor(backgroundColor)
  if (backgroundColorParam) {
    params.set("background_color", backgroundColorParam)
  }

  if (colorMode) {
    params.set("color_mode", colorMode)
  }

  const trimmedButtonLabel = buttonLabel?.trim()
  if (trimmedButtonLabel) {
    params.set("button_label", trimmedButtonLabel)
  }

  const trimmedButtonLink = buttonLink?.trim()
  if (trimmedButtonLink) {
    params.set("button_link", trimmedButtonLink)
  }

  if (buttonTarget === "same_tab" || buttonTarget === "new_tab") {
    params.set("button_target", buttonTarget)
  }

  return getEmbedURL({
    featureKey: "package",
    baseUrl,
    queryParams: params
  })
}

type PackageEmbedIframeCodeOptions = PackageEmbedURLOptions & {
  width?: number | string
  height?: number | string
  includeResizeScript?: boolean
}

export const getPackageEmbedIframeCode = (
  options: PackageEmbedIframeCodeOptions = {}
) => {
  const embedUrl = getPackageEmbedURL(options)
  const width = options.width ?? "100%"
  const height = options.height ?? 420
  const includeResizeScript = options.includeResizeScript ?? true
  const iframeCode = `<iframe src="${embedUrl.toString()}" width="${width}" height="${height}" style="border:0;overflow:hidden;display:block;max-width:100%;background:transparent;" loading="lazy"></iframe>`

  if (!includeResizeScript) {
    return iframeCode
  }

  return `${iframeCode}
<script>
;(function() {
  var script = document.currentScript
  var iframe = script && script.previousElementSibling
  if (!iframe || iframe.tagName !== "IFRAME") return

  window.addEventListener("message", function(event) {
    var data = event.data
    if (!data || data.source !== "pathwright:package-embed") return
    if (data.type !== "resize") return
    if (event.source !== iframe.contentWindow) return
    if (typeof data.height === "number" && data.height > 0) {
      iframe.style.height = data.height + "px"
    }
  })
})()
</script>`
}
